import React from "react";

// Shared UI primitives (2026-10-08 UI pass). Every page should build from these + the CSS tokens in App.css
// instead of hand-rolling buttons/cards/table headers. Inline-style objects, matching the app's existing pattern.

export const ui = {
  // .main-content (App.css) already gives every page its 20/24px gutter (12px on mobile) - pages add none of their own.
  page: { padding: 0, width: "100%", minWidth: 0 },
  card: { background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", boxShadow: "var(--shadow-sm)" },
  tableWrap: { background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "auto", boxShadow: "var(--shadow-sm)" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 13 },
  th: {
    position: "sticky", top: 0, zIndex: 1, background: "var(--bg-card)", color: "var(--text-muted)", fontSize: 11.5, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "left", padding: "9px 12px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap",
  },
  td: { padding: "9px 12px", borderBottom: "1px solid var(--border)", color: "var(--text)", verticalAlign: "top" },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  input: {
    padding: "7px 10px", fontSize: 13, border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", background: "var(--bg-input)",
    color: "var(--text)", outline: "none", transition: "border-color var(--t-fast), box-shadow var(--t-fast)",
  },
  label: { fontSize: 12, fontWeight: 600, color: "var(--text-muted)" },
  muted: { color: "var(--text-muted)" },
};

const BTN = {
  base: {
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "7px 14px", fontSize: 13, fontWeight: 600,
    borderRadius: "var(--radius-sm)", borderWidth: 1, borderStyle: "solid", borderColor: "transparent", cursor: "pointer", lineHeight: 1.2, whiteSpace: "nowrap",
    transition: "background var(--t-fast), border-color var(--t-fast), color var(--t-fast), opacity var(--t-fast)",
  },
  primary: { background: "var(--accent)", color: "#fff" },
  secondary: { background: "var(--card)", color: "var(--text)", borderColor: "var(--border-strong)" },
  ghost: { background: "transparent", color: "var(--text-muted)" },
  danger: { background: "var(--danger)", color: "#fff" },
  success: { background: "var(--success)", color: "#fff" },
  sm: { padding: "4px 10px", fontSize: 12 },
};
const HOVER = { primary: "var(--accent-hover)", secondary: "var(--bg-hover)", ghost: "var(--bg-hover)", danger: "var(--danger)", success: "var(--success)" };

// <Button variant="primary|secondary|ghost|danger|success" size="sm">
export function Button({ variant = "secondary", size, style, disabled, children, ...rest }) {
  const s = { ...BTN.base, ...BTN[variant], ...(size === "sm" ? BTN.sm : null), ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : null), ...style };
  return (
    <button type="button" disabled={disabled} style={s} {...rest}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = HOVER[variant]; rest.onMouseEnter && rest.onMouseEnter(e); }}
      onMouseLeave={(e) => { e.currentTarget.style.background = s.background; rest.onMouseLeave && rest.onMouseLeave(e); }}>
      {children}
    </button>
  );
}

export function Card({ title, actions, children, style, bodyStyle }) {
  return (
    <section style={{ ...ui.card, ...style }}>
      {(title || actions) && (
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
          {title && <h3 style={{ fontSize: 14, fontWeight: 650, color: "var(--text)", margin: 0 }}>{title}</h3>}
          {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
        </header>
      )}
      <div style={{ padding: 16, ...bodyStyle }}>{children}</div>
    </section>
  );
}

// Page title row: title on the left, actions on the right. Keep `subtitle` short or omit it.
export function PageHeader({ title, subtitle, actions, children }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.2, color: "var(--text)", margin: 0 }}>{title}</h1>
        {subtitle && <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{subtitle}</div>}
      </div>
      {(actions || children) && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>{actions}{children}</div>}
    </div>
  );
}

const TONES = {
  neutral: ["var(--bg-hover)", "var(--text-muted)"], accent: ["var(--accent-soft)", "var(--accent)"], success: ["var(--success-bg)", "var(--success)"],
  danger: ["var(--danger-bg)", "var(--danger)"], warning: ["var(--warning-bg)", "var(--warning)"],
};
// Row hover for table rows: <tr {...rowHover}>.
export const rowHover = {
  onMouseEnter: (e) => { e.currentTarget.style.background = "var(--bg-hover)"; },
  onMouseLeave: (e) => { e.currentTarget.style.background = ""; },
};

export function Badge({ tone = "neutral", children, title, style }) {
  const [bg, fg] = TONES[tone] || TONES.neutral;
  return <span title={title} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: bg, color: fg, whiteSpace: "nowrap", ...style }}>{children}</span>;
}

// KPI tile. `tone` colours the value only.
export function Stat({ label, value, hint, tone }) {
  const color = tone ? (TONES[tone] || TONES.neutral)[1] : "var(--text)";
  return (
    <div style={{ ...ui.card, padding: "12px 14px", minWidth: 0 }}>
      <div style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginTop: 4, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{value}</div>
      {hint && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

// Click-to-sort header cell. sort = { key, dir: "asc"|"desc" }; onSort(key) toggles. Paginated tables must sort server-side.
export function SortTh({ label, sortKey, sort, onSort, align, style, title, className }) {
  const active = sort && sort.key === sortKey;
  const clickable = !!(onSort && sortKey);
  return (
    <th className={className} title={title} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}
      onClick={clickable ? () => onSort(sortKey) : undefined}
      style={{ ...ui.th, textAlign: align || "left", cursor: clickable ? "pointer" : "default", userSelect: "none", color: active ? "var(--text)" : ui.th.color, ...style }}>
      {label}{clickable && <span aria-hidden="true" style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.35 }}>{active ? (sort.dir === "asc" ? "▲" : "▼") : "▲▼"}</span>}
    </th>
  );
}

export function EmptyState({ title = "Nothing here yet", children, icon = "○" }) {
  return (
    <div style={{ padding: "36px 16px", textAlign: "center", color: "var(--text-muted)" }}>
      <div aria-hidden="true" style={{ fontSize: 22, opacity: 0.6, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{title}</div>
      {children && <div style={{ fontSize: 13, marginTop: 4 }}>{children}</div>}
    </div>
  );
}

// Error block - never leave blank space when a load fails.
export function ErrorState({ message, onRetry }) {
  return (
    <div role="alert" style={{ padding: "12px 14px", borderRadius: "var(--radius)", background: "var(--danger-bg)", color: "var(--danger)", fontSize: 13, display: "flex", gap: 12, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
      <span style={{ overflowWrap: "anywhere" }}>{message || "Something went wrong while loading."}</span>
      {onRetry && <Button size="sm" variant="secondary" onClick={onRetry}>Retry</Button>}
    </div>
  );
}

export function Spinner({ size = 16 }) {
  return <span aria-label="Loading" role="status" style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", border: "2px solid var(--border-strong)", borderTopColor: "var(--accent)", animation: "ui-spin .7s linear infinite", verticalAlign: "middle" }} />;
}

// Thin indeterminate bar for loads > 2s; place at the top of a card/table.
export function LoadingBar() {
  return (
    <div role="progressbar" aria-label="Loading" style={{ position: "relative", height: 3, overflow: "hidden", background: "var(--accent-soft)", borderRadius: 2 }}>
      <div style={{ position: "absolute", top: 0, bottom: 0, background: "var(--accent)", borderRadius: 2, animation: "ui-indeterminate 1.1s ease-in-out infinite" }} />
    </div>
  );
}

// Skeleton rows for tables while the first page loads.
export function SkeletonRows({ rows = 6, cols = 5 }) {
  const cell = { height: 12, borderRadius: 4, background: "linear-gradient(90deg, var(--bg-hover) 0%, var(--border) 50%, var(--bg-hover) 100%)", backgroundSize: "800px 100%", animation: "ui-shimmer 1.2s linear infinite" };
  return (
    <tbody aria-hidden="true">
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>{Array.from({ length: cols }).map((__, c) => <td key={c} style={ui.td}><div style={{ ...cell, width: `${55 + ((r * 7 + c * 13) % 40)}%` }} /></td>)}</tr>
      ))}
    </tbody>
  );
}

// On/off switch. checked + onChange(next). Same look as CP Configuration's profitability switch.
export function Switch({ checked, onChange, disabled, label, title, size = "md" }) {
  const w = size === "sm" ? 34 : 44, h = size === "sm" ? 20 : 24, k = h - 6;
  return (
    <button type="button" role="switch" aria-checked={!!checked} aria-label={label} title={title} disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{ width: w, height: h, borderRadius: h / 2, borderWidth: 1, borderStyle: "solid", borderColor: checked ? "var(--accent)" : "var(--border-strong)", padding: 0, position: "relative", flexShrink: 0,
        cursor: disabled ? "not-allowed" : "pointer", background: checked ? "var(--accent)" : "var(--bg-hover)", opacity: disabled ? 0.55 : 1, transition: "background var(--t-fast), border-color var(--t-fast)" }}>
      <span style={{ position: "absolute", top: 2, left: checked ? w - k - 4 : 2, width: k, height: k, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.35)", transition: "left var(--t-fast)" }} />
    </button>
  );
}

// Segmented control (e.g. SKU | Style). options = [{ value, label, disabled?, title? }].
// 2026-10-17: optional per-option disabled + title (hover tooltip, e.g. why an option is off); size "sm" for dense rows.
// A disabled option uses aria-disabled (not the disabled attribute) so its title tooltip still shows on hover.
export function Segmented({ options, value, onChange, ariaLabel, size }) {
  const sm = size === "sm";
  return (
    <div role="tablist" aria-label={ariaLabel} style={{ display: "inline-flex", padding: 2, gap: 2, borderRadius: "var(--radius-sm)", background: "var(--bg-hover)", border: "1px solid var(--border)" }}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" role="tab" aria-selected={on} aria-disabled={o.disabled || undefined} title={o.title}
            onClick={() => !o.disabled && onChange(o.value)}
            style={{ padding: sm ? "2px 8px" : "4px 12px", fontSize: sm ? 11.5 : 12.5, fontWeight: 600, border: "none", borderRadius: 4, cursor: o.disabled ? "not-allowed" : "pointer", opacity: o.disabled ? 0.5 : 1, background: on ? "var(--card)" : "transparent", color: on ? "var(--text)" : "var(--text-muted)", boxShadow: on ? "var(--shadow-sm)" : "none", transition: "background var(--t-fast), color var(--t-fast)" }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// Tab strip for in-page sections. tabs = [{ id, label, count? }].
export function Tabs({ tabs, value, onChange }) {
  return (
    <div role="tablist" style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--border)", marginBottom: 16, overflowX: "auto" }}>
      {tabs.map((t) => {
        const on = t.id === value;
        return (
          <button key={t.id} type="button" role="tab" aria-selected={on} onClick={() => onChange(t.id)}
            style={{ padding: "9px 14px", fontSize: 13, fontWeight: 600, background: "none", border: "none", borderBottom: `2px solid ${on ? "var(--accent)" : "transparent"}`, marginBottom: -1, color: on ? "var(--text)" : "var(--text-muted)", cursor: "pointer", whiteSpace: "nowrap", transition: "color var(--t-fast), border-color var(--t-fast)" }}>
            {t.label}{t.count != null && <span style={{ marginLeft: 6, fontSize: 11, padding: "1px 6px", borderRadius: 999, background: on ? "var(--accent-soft)" : "var(--bg-hover)", color: on ? "var(--accent)" : "var(--text-muted)" }}>{Number(t.count).toLocaleString("en-IN")}</span>}
          </button>
        );
      })}
    </div>
  );
}
